import { createRouter, createWebHistory } from 'vue-router';
import { externalDisabledRoutes } from '@/utils/index.js';

import themeService from '@/services/themeService.js';
const $theme = themeService;

import sessionService from '@/services/sessionService.js';
const defaultPath = window.rmsBasePath;

const routes = [
    // Auth
    {
        path: '/',
        component: () => import('@/views/AuthLayout.vue' /* webpackChunkName: "login" */),
        children: [
            {
                name: 'auth.login',
                path: '/',
                component: () => import('@/views/Login.vue' /* webpackChunkName: "login" */)
            },
            {
                name: 'auth.register',
                path: '/register/:token?',
                component: () => import('@/views/Register.vue' /* webpackChunkName: "login" */)
            },
            {
                name: 'auth.forgot-pass',
                path: '/forgot-password',
                component: () => import('@/views/ForgotPassword.vue' /* webpackChunkName: "login" */)
            },
            {
                name: 'auth.invite',
                path: '/email-invite',
                component: () => import('@/views/EmailInvite.vue' /* webpackChunkName: "login" */)
            },
            {
                name: 'auth.reset-pass',
                path: '/reset-password/:token',
                component: () => import('@/views/ResetPassword.vue' /* webpackChunkName: "login" */)
            },
            {
                name: 'auth.activate',
                path: '/activate/:token',
                component: () => import('@/views/ActivateUser.vue' /* webpackChunkName: "login" */)
            },
            {
                name: 'auth.sign-in-with-sso',
                path: '/sign-in-with-sso',
                component: () => import('@/views/LoginSSO.vue' /* webpackChunkName: "login" */)
            },
        ]
    },

    // Settings
    {
        path: "/settings/profile",
        name: 'settings.general',
        component: () => import('@/views/SettingsLayout.vue' /* webpackChunkName: "settings" */),
        meta: {
            requiresAuth: true
        },
        children: [
            {
                path: '/settings/profile',
                name: 'settings.profile',
                component: () => import('@/views/Profile.vue' /* webpackChunkName: "profile" */),
                meta: {
                    requiresAuth: true,
                }
            },
            {
                path: '/settings/security',
                name: 'settings.security',
                component: () => import('@/views/Security.vue' /* webpackChunkName: "security" */),
                meta: {
                    requiresAuth: true,
                }
            },
            {
                path: '/settings/connections',
                name: 'settings.connections',
                component: () => import('@/views/Connections.vue' /* webpackChunkName: "connections" */),
                meta: {
                    requiresAuth: true,
                }
            },
            {
                path: '/settings/applications',
                name: 'settings.applications',
                component: () => import('@/views/Applications.vue' /* webpackChunkName: "applications" */),
                meta: {
                    requiresAuth: true,
                }
            },
            {
                path: '/settings/application/:id?',
                name: 'settings.applications.form',
                component: () => import('@/views/ApplicationForm.vue' /* webpackChunkName: "applications" */),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/settings/authorized-applications',
                name: 'settings.authorized',
                component: () => import('@/views/Authorized.vue' /* webpackChunkName: "authorized" */),
                meta: {
                    requiresAuth: true,
                }
            },
            {
                path: '/settings/tokens',
                name: 'settings.tokens',
                component: () => import('@/views/Tokens.vue' /* webpackChunkName: "tokens" */),
                meta: {
                    requiresAuth: true,
                }
            },
            {
                path: '/settings/token/:id?',
                name: 'settings.tokens.form',
                component: () => import('@/views/TokenForm.vue' /* webpackChunkName: "applications" */),
                meta: {
                    requiresAuth: true,
                },
            },
            {
                path: '/settings/mfa-required',
                name: 'mfa.required',
                component: () => import('@/views/MFARequired.vue' /* webpackChunkName: "settings" */),
                meta: {
                    requiresAuth: true,
                }
            },
        ]
    },

    // Standalone
    {
        name: 'change-email',
        path: '/change-email/:token',
        component: () => import('@/views/ChangeEmail.vue' /* webpackChunkName: "email" */),
        meta: {
            standalone: true
        }
    },
    {
        name: 'login.redirect',
        path: '/login/redirect',
        component: () => import('@/views/Redirect.vue' /* webpackChunkName: "redirect" */),
        meta: {
            standalone: true,
            requiresAuth: true,
        }
    },

    // Policy
    {
        path: '/privacy-policy',
        component: () => import('@/views/PolicyLayout.vue' /* webpackChunkName: "policy" */),
        children: [
            {
                name: 'privacy-policy',
                path: '/privacy-policy',
                component: () => import('@/views/policies/PrivacyPolicy.vue' /* webpackChunkName: "policy" */),
                meta: {
                    isPolicy: true
                }
            },
            {
                name: 'terms-of-service',
                path: '/terms-of-service',
                component: () => import('@/views/policies/TermsOfService.vue' /* webpackChunkName: "policy" */),
                meta: {
                    isPolicy: true
                }
            },
            {
                name: 'data-agreement',
                path: '/data-processing-agreement',
                component: () => import('@/views/policies/DataAgreement.vue' /* webpackChunkName: "policy" */),
                meta: {
                    isPolicy: true
                }
            }
        ]
    },
     // Social/SSO Callback
    {
        name: 'social.callback',
        path: '/login/social/:provider/callback',
        component: () => import('@/views/Callback.vue' /* webpackChunkName: "login" */)
    },
    {
        name: 'sso.callback',
        path: '/sign-in/sso/callback',
        component: () => import('@/views/Callback.vue' /* webpackChunkName: "login" */)
    },
    // Fallback route
    {
        path: "/:catchAll(.*)",
        redirect: sessionService.isAuthenticated ? `/settings/profile` : '/'
    }
];

export const createGlobalRouter = (app) => {
    const router = createRouter({
        history: createWebHistory(defaultPath ? defaultPath : '/'),
        linkExactActiveClass: 'link-active',
        routes: routes
    });

    router.beforeEach(async(to, from, next) => {
        if(to?.meta?.isPolicy) {
            if($theme.default && process.env.NODE_ENV === 'production') return next();

            return next({ path: '/' });
        }
        else if (to.meta.requiresAuth) {
            if(!sessionService.user) {
                await sessionService.initSession(app);
            }

            if(!sessionService.isAuthenticated) {
                return next({ path: '/' });
            }

            if(sessionService.user.external && externalDisabledRoutes.includes(to.name)) {
                return next({ path: '/settings/profile' });
            }

            if($theme.auth_disabled_routes.includes(to.name)) {
                return next({ path: '/settings/profile' });
            }

            return next();
        }
        else if(to?.name == "auth.register" && (to?.query?.platform == 'mobile-review' || ($theme?.disable_user_registration && !to?.params?.token && !to?.query?.error))) {
            return next({ path: '/', query: to.query });
        }
        else {
            next();
        }
    });

    router.onError(error => {
        if(!error) return;
        console.error(error);
        if(error?.name === "ChunkLoadError" && /loading chunk.+ failed\./i.test(error?.message)){
            window.location.reload()
        }
    });

    return router;
}